import { Link, useLocation } from "react-router-dom";
import { Heart, MessageCircle, Users, UserPlus, User } from "lucide-react";

/**
 * Fixed mobile bottom tab bar — the only navigation surface on small viewports.
 *
 * Why a bottom bar (vs. a hamburger)? This is a dating app — thumb-reach matters
 * and the 5 core destinations are the *whole* product. Hamburgers hide them.
 * Visible on screens < md and only for onboarded users; the desktop top-nav in
 * `Header.jsx` still owns md+ and the unauthenticated marketing nav.
 *
 * Props:
 *   unread — number to render on the Messages tab badge.
 */
const TABS = [
  { to: "/discover",    label: "Discover",  icon: Heart,         test: "tab-discover",    match: (p) => p === "/discover" },
  { to: "/messages",    label: "Messages",  icon: MessageCircle, test: "tab-messages",    match: (p) => p.startsWith("/messages") || p.startsWith("/chat") },
  { to: "/doppel-room", label: "Doppel",    icon: Users,         test: "tab-doppel-room", match: (p) => p.startsWith("/doppel-room") || p.startsWith("/compare") },
  { to: "/invite",      label: "Invite",    icon: UserPlus,      test: "tab-invite",      match: (p) => p.startsWith("/invite") },
  { to: "/profile",     label: "Profile",   icon: User,          test: "tab-profile",     match: (p) => p.startsWith("/profile") },
];

export default function MobileTabBar({ unread = 0 }) {
  const { pathname } = useLocation();
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur-md border-t-2 border-pink-100 shadow-[0_-6px_24px_rgba(255,45,138,0.08)]"
      data-testid="mobile-tab-bar"
      aria-label="Primary"
    >
      <ul className="grid grid-cols-5 items-stretch">
        {TABS.map(({ to, label, icon: Icon, test, match }) => {
          const active = match(pathname);
          return (
            <li key={to} className="contents">
              <Link
                to={to}
                aria-current={active ? "page" : undefined}
                className="relative flex flex-col items-center justify-center gap-0.5 py-2.5 select-none"
                data-testid={test}
              >
                <span
                  className={`relative grid place-items-center w-9 h-9 rounded-xl transition-colors ${
                    active ? "bg-pink-100" : "bg-transparent"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${active ? "text-pink-600" : "text-slate-500"}`}
                    strokeWidth={active ? 2.4 : 2}
                  />
                  {test === "tab-messages" && unread > 0 ? (
                    <span
                      className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-pink-500 text-white text-[9px] font-display font-bold grid place-items-center border border-white"
                      data-testid="tab-unread-badge"
                    >
                      {unread > 9 ? "9+" : unread}
                    </span>
                  ) : null}
                </span>
                <span
                  className={`text-[10px] leading-none font-display font-bold ${
                    active ? "text-pink-600" : "text-slate-500"
                  }`}
                >
                  {label}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      {/* iOS safe-area padding so the tab bar clears the home indicator. */}
      <div className="h-[env(safe-area-inset-bottom)]" aria-hidden />
    </nav>
  );
}
